import { ampli } from '@/ampli';

import { ClientNetworkError, ClientWalletError } from '@/lib/errors/client';

import { fillOrder } from '@/components/TransactionModule/fillOrder';

import { CHAIN_IDS, SUPPORTED_CHAINS_BY_CHAIN_ID } from '@/constants/chain';

import type { Price, Quote, SwapQuote } from '@/utils/0x.types';
import { trackError } from '@/utils/errors';
import type { EthereumAddress } from '@/utils/models';
import { getSubmissionReason } from '@/utils/submissionReason';
import { trackTransactionFailed } from '@/utils/transactionAnalytics';
import {
  getPrivyWalletClient,
  isUserRejection,
  reportWalletGuardDiagnostics,
} from '@/utils/wallet';

import type { FailureEvent } from '../analytics';
import { buildTokenEventFields, matchaReferrer } from '../analytics';
import type { ExecutionContext, PreflightResult, TradeStrategy } from '../types';

type ExecutableWrapQuote = SwapQuote & { taker: EthereumAddress };
type PreparedWrapTrade = PreflightResult & {
  raw: Price | Quote | undefined;
};

function wrapFailure(
  ctx: ExecutionContext,
  details: Pick<FailureEvent, 'failureStage' | 'failureReason' | 'submissionReason'>,
): FailureEvent {
  return {
    sellToken: ctx.sellToken,
    buyToken: ctx.buyToken,
    swapOrderType: 'wrap',
    matchaReferrer: matchaReferrer(ctx),
    recipientAddress: ctx.recipientAddress,
    ...details,
  };
}

/** Native-token wrap and unwrap strategy, including the Abstract gas-field workaround. */
export const wrapStrategy: TradeStrategy<PreparedWrapTrade> = {
  lane: 'wrap',
  preflight: (ctx) =>
    ctx.quote.source === 'swap' ? { chainFamily: 'evm', raw: ctx.quote.raw } : null,
  submit: async (ctx, prepared) => {
    ampli.swapInitiated({
      ...buildTokenEventFields({
        source: 'input',
        sellToken: ctx.sellToken,
        buyToken: ctx.buyToken,
        sellInput: ctx.sellInput,
        buyInput: ctx.buyInput,
      }),
      matcha_referrer: matchaReferrer(ctx),
      recipient_address: ctx.recipientAddress,
      swap_order_type: 'wrap',
      swap_execution_type: undefined,
    });

    const chain = SUPPORTED_CHAINS_BY_CHAIN_ID[ctx.chainId];
    const client = chain ? await getPrivyWalletClient(ctx.ethereumWallet, chain) : undefined;

    if (!client) {
      reportWalletGuardDiagnostics({
        ethereumWallet: ctx.ethereumWallet,
        targetChainId: ctx.sellToken.chainId,
        accountAddress: ctx.accountAddress,
        walletClientSource: 'privy_direct',
      });
      trackTransactionFailed(
        wrapFailure(ctx, {
          failureStage: 'pre_submission',
          failureReason: 'wallet_not_ready',
        }),
      );
      return {
        success: false,
        rejected: false,
        error: new ClientWalletError(
          'Wallet connection error. Please try again or reconnect your wallet.',
        ),
      };
    }
    if (
      !ctx.accountAddress ||
      !prepared.raw ||
      !('transaction' in prepared.raw) ||
      !('taker' in prepared.raw)
    ) {
      const error = new ClientNetworkError('Unhandled silent error in handleSubmitWrap');
      trackError(error);
      return { success: false, rejected: false, error };
    }

    try {
      if (prepared.raw.taker !== ctx.accountAddress) throw new Error('taker mismatch');
      const quote: ExecutableWrapQuote = prepared.raw;

      const transaction =
        ctx.chainId === CHAIN_IDS.ABSTRACT
          ? { ...quote.transaction, gas: null, gasPrice: '0' }
          : quote.transaction;
      const hash = await fillOrder({
        transaction,
        chainId: ctx.chainId,
        walletClient: client,
        connectorId: ctx.ethereumWallet?.meta.id,
      });

      const tokenFields = buildTokenEventFields({
        source: 'quote',
        sellToken: ctx.sellToken,
        buyToken: ctx.buyToken,
        sellAmount: quote.sellAmount,
        buyAmount: quote.buyAmount,
        tokenUsdPrices: ctx.tokenUsdPrices,
      });
      ctx.deps.addTransaction(
        {
          sellToken: ctx.sellToken,
          sellAmount: quote.sellAmount,
          sellTokenUsdPrice: ctx.tokenUsdPrices.sellTokenUsd,
          sellAmountUsd: tokenFields.sell_token_amount_usd,
          buyToken: ctx.buyToken,
          buyAmount: quote.buyAmount,
          buyTokenUsdPrice: ctx.tokenUsdPrices.buyTokenUsd,
          buyAmountUsd: tokenFields.buy_token_amount_usd,
          feeSource: quote.fees.zeroExFee,
          referrerId: matchaReferrer(ctx),
          hash,
          timestamp: new Date().toISOString(),
          transactionType: 'wrap',
        },
        ctx.accountAddress,
        ctx.chainId,
      );
      ampli.transactionSubmitted({
        ...tokenFields,
        is_smart_wallet: ctx.isSmartWallet,
        matcha_referrer: matchaReferrer(ctx),
        recipient_address: ctx.recipientAddress,
        swap_order_type: 'wrap',
        swap_tx_hash: hash,
      });
      return { success: true, outcome: { kind: 'txHash', value: hash } };
    } catch (error: unknown) {
      const rejected = isUserRejection(error);
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      trackTransactionFailed(
        wrapFailure(ctx, {
          failureStage: 'submission',
          failureReason: rejected ? 'user_rejected' : 'submission_error',
          submissionReason: rejected ? undefined : getSubmissionReason(normalizedError),
        }),
      );
      if (!rejected) {
        trackError(new ClientNetworkError(`[SubmitWrapError]: ${normalizedError.message}`));
      }
      return { success: false, rejected, error: normalizedError };
    }
  },
};
